import { isOrderClosed, type OrderEventPayload, type OrderQueueItem, type OrderStatus, type RealtimeEvent } from '@cuito/types';

/**
 * 本地订单队列的**纯函数层**（计划 §4.4 冻结签名）。
 *
 * 三条通道写进同一张表：
 *   · WS 事件（`applyOrderEvent`）
 *   · 轮询快照（`mergeSnapshot`）
 *   · 重连补发（`mergeOrderPayloads`）
 * 因此每条函数都必须**幂等**：重复到达不产生副作用，这是 §4.2 发射纪律第 3 条的客户端一半。
 *
 * 队列项**复用** `@cuito/types` 的 `OrderQueueItem`（与 `GET /orders` 的 `items[]` 同一投影），
 * 不在本目录另立一份同形接口：两份同形类型会各自漂移，而「件数」这类字段正是漂移的落点。
 */
export type StreamLevel = 'live' | 'polling' | 'manual';

export interface OrderStreamState {
  level: StreamLevel;
  orders: Record<string, OrderQueueItem>;
  lastEventAt: string | null;
}

/** 接单面板的四个分区（§3.3 `/cashier`）；顺序即页面上 tab 的顺序 */
export const BOARD_SECTIONS = ['PENDING', 'ACCEPTED', 'PICKING', 'READY'] as const;
export type BoardSection = (typeof BOARD_SECTIONS)[number];

/** 分区 tab 与状态徽标的文案（§7.4 术语表：待接单 / 已接单 / 挑拣中 / 可交付） */
export const SECTION_LABELS: Record<BoardSection, string> = {
  PENDING: '待接单',
  ACCEPTED: '已接单',
  PICKING: '挑拣中',
  READY: '可交付',
};

/** 已关闭的单留在队列里多久（到期后由 `pruneClosedOrders` 回收） */
export const CLOSED_KEEP_MS = 5 * 60_000;

export function initialState(level: StreamLevel = 'live'): OrderStreamState {
  return { level, orders: {}, lastEventAt: null };
}

/**
 * 件数（`itemCount` = `Σ lines[].quantity`）的取值。
 *
 * 事件载荷**没有** `itemCount` 字段（§19.4 只有 `lineCount` / `pickedLineCount`），因此：
 *   · 载荷**带** `lines` 时按行清单求和 —— 这是唯一的真值来源；
 *   · 载荷**省略** `lines` 时（进度类事件与`joined.changedOrders[]`，A19）沿用上一份快照 ——
 *     「字段缺席」不等于「件数为 0」，把它当成 0 会让卡片上的「N 件」在每次进度事件后归零；
 *   · 两者都没有（第一次见到的单就是进度事件）才退回 `lineCount`：件数恒 ≥ 行数，
 *     宁可暂时偏小也不谎报 0 件，服务端的轮询快照与详情随后会补齐真值。
 */
function itemCountOf(payload: OrderEventPayload, previous: OrderQueueItem | undefined): number {
  if (payload.lines !== undefined) {
    return payload.lines.reduce((sum, entry) => sum + entry.quantity, 0);
  }
  return previous?.itemCount ?? payload.lineCount;
}

/**
 * `OrderEventPayload` → 队列项。
 *
 * 两种形状的字段名不同（`orderId` vs `id`）是**服务端两套投影的既有事实**：
 * 事件载荷用 `orderId`（§19.4），队列项用 `id`（§12.2 `GET /orders` 的 `items[]`）。
 * 投影只在这一处做，页面上不再出现 `payload.orderId` 与 `item.id` 的混用。
 */
function toQueueItem(payload: OrderEventPayload, previous: OrderQueueItem | undefined): OrderQueueItem {
  return {
    id: payload.orderId,
    orderNo: payload.orderNo,
    shortCode: payload.shortCode,
    status: payload.status,
    channel: payload.channel,
    totalAmount: payload.totalAmount,
    lineCount: payload.lineCount,
    itemCount: itemCountOf(payload, previous),
    pickedLineCount: payload.pickedLineCount,
    paidConfirmed: payload.paidConfirmed,
    createdAt: payload.createdAt,
    expiresAt: payload.expiresAt,
  };
}

/**
 * 应用一个 WS 事件：**以 `orderId` 为键、后写覆盖**（§4.2 第 3 条）。
 *
 * ⚠️ 载荷里**没有 `updatedAt`**（§19.4 的 `OrderEventPayload` 只有 `createdAt` / `expiresAt`），
 * 因此客户端**无法**做「按时间戳比新旧」的全序比较。这里只钉一条边界：
 * **已关闭的单不允许被迟到的旧事件复活**（`COMPLETED` 之后到达的 `PENDING` 推送会把一张
 * 已交付的单重新插回待接单 —— 那是最糟的一类幽灵卡）。同状态或都未关闭时严格后写覆盖。
 */
export function applyOrderEvent(
  state: OrderStreamState,
  event: RealtimeEvent,
  payload: OrderEventPayload,
): OrderStreamState {
  // `stock:changed` 不是订单事件（§19.3）：它由库存页消费，不该改订单表
  if (event === 'stock:changed') return state;

  const existing = state.orders[payload.orderId];
  const lastEventAt = new Date().toISOString();

  if (existing !== undefined && isOrderClosed(existing.status) && !isOrderClosed(payload.status)) {
    return { ...state, lastEventAt };
  }

  return {
    ...state,
    lastEventAt,
    orders: { ...state.orders, [payload.orderId]: toQueueItem(payload, existing) },
  };
}

/**
 * 合并重连补发的 `joined.changedOrders`（§4.6 重连补发，`OrderEventPayload[]`）。
 *
 * 与 `mergeSnapshot` 分开的两个理由：① 元素形状不同（载荷 vs 队列项）；
 * ② 语义不同 —— 补发列表**不是全集**，因此**不能**用它删任何东西。
 *
 * 列表元素**省略 `lines`**（A19）：合并时沿用上一份快照的行派生字段（件数），
 * 不把「缺席」当成「没有行」。
 */
export function mergeOrderPayloads(
  state: OrderStreamState,
  payloads: readonly OrderEventPayload[],
): OrderStreamState {
  if (payloads.length === 0) return state;
  const orders = { ...state.orders };
  for (const payload of payloads) {
    const existing = orders[payload.orderId];
    if (existing !== undefined && isOrderClosed(existing.status) && !isOrderClosed(payload.status)) continue;
    orders[payload.orderId] = toQueueItem(payload, existing);
  }
  return { ...state, orders };
}

/**
 * 合并一次轮询快照。
 *
 * **语义是「这次快照代表这些 PENDING 单的全集」**：本地仍标 `PENDING`、却不在快照里的单，
 * 说明它已经不是待接单了（被别人接走 / 已取消）⇒ 移出队列并让卡片消失（§3.3 的错误态
 * 「接单失败：该订单已被其他收银端接单」走的是同一条判据）。非 PENDING 的单不受影响 ——
 * 快照（`status=PENDING`）对它们没有任何信息量，拿它删单会丢掉店员正在处理的单。
 */
export function mergeSnapshot(
  state: OrderStreamState,
  items: readonly OrderQueueItem[],
): OrderStreamState {
  const orders: Record<string, OrderQueueItem> = { ...state.orders };
  const seen = new Set<string>();
  for (const entry of items) {
    orders[entry.id] = entry;
    seen.add(entry.id);
  }
  for (const [orderId, order] of Object.entries(orders)) {
    if (order.status === 'PENDING' && !seen.has(orderId)) delete orders[orderId];
  }
  return { ...state, orders };
}

/**
 * 回收已关闭的单。
 *
 * **判据取 `expiresAt`**：`OrderQueueItem` 里只有 `createdAt` / `expiresAt` 两个时间戳
 * （§12.2），而 `expiresAt = createdAt + 下单 TTL`，是「这张单有多大年纪」的单调代理。
 * 关键在**不能误删未完成单**：`PENDING` / `ACCEPTED` / `PICKING` / `READY` 无论多老都留着，
 * 它们是店员还要动手的活；只有 `isOrderClosed`（`COMPLETED` / `CANCELLED` / `REFUNDED`）
 * 才参与回收 —— 那正是「看板已经没事可做」的那一类。
 *
 * `expiresAt` 为 `null`（已交付/已取消的派生值，A19）时**不回收**：没有年龄就不猜年龄，
 * 猜错的代价是把一张店员正在看的卡删掉。
 */
export function pruneClosedOrders(state: OrderStreamState, keepClosedMs = CLOSED_KEEP_MS): OrderStreamState {
  const now = Date.now();
  const orders: Record<string, OrderQueueItem> = {};
  for (const [orderId, order] of Object.entries(state.orders)) {
    const expiresAt = order.expiresAt === null ? Number.NaN : Date.parse(order.expiresAt);
    const recyclable =
      isOrderClosed(order.status) && Number.isFinite(expiresAt) && expiresAt + keepClosedMs <= now;
    if (!recyclable) orders[orderId] = order;
  }
  return { ...state, orders };
}

/** 分区归类（`createdAt` 降序 = 最新在前，与 §12.2 `GET /orders` 的排序一致） */
export function groupBySection(
  orders: readonly OrderQueueItem[],
): Record<BoardSection, OrderQueueItem[]> {
  const result: Record<BoardSection, OrderQueueItem[]> = {
    PENDING: [],
    ACCEPTED: [],
    PICKING: [],
    READY: [],
  };
  for (const order of orders) {
    if ((BOARD_SECTIONS as readonly OrderStatus[]).includes(order.status)) {
      result[order.status as BoardSection].push(order);
    }
  }
  for (const section of BOARD_SECTIONS) {
    result[section].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
  }
  return result;
}
